import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { CreditCardOnFile } from '@liveauctioneers/hammer-ui-payments/checkout';
import { useEffect, useMemo, useState } from 'react';

import { usePrevious } from '@/hooks/usePrevious';
import { CreditCard } from '@/types/CreditCard';

type Props = {
    creditCards: CreditCard[];
    deleteCard?: (cardId: number) => void;
    selectCard: (creditCard: CreditCard) => void;
};

const CardSelectionComponent = ({ creditCards, deleteCard, selectCard }: Props) => {
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>();
    const previousNumberOfCards = usePrevious(creditCards?.length);

    useEffect(() => {
        if (Boolean(creditCards) && (!selectedPaymentMethodId || previousNumberOfCards !== creditCards?.length)) {
            const defaultCardOnFile = creditCards.find((cof) => cof.defaultCard);
            setSelectedPaymentMethodId(`card-${defaultCardOnFile?.id}`);
            if (defaultCardOnFile && selectCard) {
                selectCard(defaultCardOnFile);
            }
        }
    }, [creditCards, previousNumberOfCards, selectCard, selectedPaymentMethodId]);

    const creditCardRadios = useMemo(() => {
        if (!creditCards) {
            return null;
        }
        const sortedcreditCards = creditCards.sort((cardA, cardB) => {
            return new Date(cardB.created).getTime() - new Date(cardA.created).getTime();
        });
        return sortedcreditCards.map((creditCard) => {
            const radioId = `card-${creditCard.id}`;
            const selected = selectedPaymentMethodId === radioId;
            return (
                <Radio.Option
                    checked={selected}
                    className="mb-md w-full items-start pt-xs pl-md [&_input]:mt-[36px]"
                    key={creditCard.id}
                    name="creditCard"
                    onChange={() => {
                        setSelectedPaymentMethodId(radioId);
                        selectCard(creditCard);
                    }}
                    value={radioId}
                >
                    <div>
                        <CreditCardOnFile
                            brand={creditCard.brand}
                            expiration={`${creditCard.month}/${creditCard.year}`}
                            isDefault={creditCard.defaultCard}
                            lastFour={creditCard.last4}
                            onRemoveClicked={() => {
                                if (selected) {
                                    setSelectedPaymentMethodId(undefined);
                                }
                                deleteCard?.(creditCard.id);
                            }}
                        />
                    </div>
                </Radio.Option>
            );
        });
    }, [creditCards, selectedPaymentMethodId, deleteCard, selectCard]);

    if (!creditCardRadios?.length) {
        return null;
    }

    return <div>{creditCardRadios}</div>;
};

export const CardSelection = CardSelectionComponent;
